import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'components/Button';
import { Select } from 'components/select/Select';
import { SelectConfirmButton, SelectFooter } from 'components/select/SelectFooter';
import { TreeSelectContent } from './TreeSelectContent';
import type { TreeItemWithIcon } from './types';

interface TreeSelectProps {
  label: string;
  items: TreeItemWithIcon[];
  selectedItems: string[];
  disabled?: boolean;
  setSelectedItems?: (itemIds: string[]) => void;
  className?: string;
  id?: string;
  showConfirmButton?: boolean;
  defaultExpandedItems?: string[];
}

export function TreeSelect({
  label,
  items,
  selectedItems,
  disabled = undefined,
  className = undefined,
  setSelectedItems = undefined,
  showConfirmButton = undefined,
  defaultExpandedItems = [],
  id = '',
}: TreeSelectProps) {
  const intl = useIntl();
  const [filter, setFilter] = useState('');
  const [expandedItems, setExpandedItems] = useState(defaultExpandedItems);
  const [orderedSelectedItems, setOrderedSelectedItems] = useState(selectedItems);
  const defaultExpandedItemsKey = defaultExpandedItems.join('|');
  useEffect(() => {
    setExpandedItems(defaultExpandedItems);
  }, [defaultExpandedItemsKey]);

  const selectedItemsSet = useMemo(() => new Set(selectedItems), [selectedItems]);
  const expandedItemsSet = useMemo(() => new Set(expandedItems), [expandedItems]);
  const index = useMemo(() => {
    const result = new Map<string, { item: TreeItemWithIcon; parentId?: string }>();
    const visit = (nodes: TreeItemWithIcon[], parentId?: string) =>
      nodes.forEach(item => {
        result.set(item.id, { item, parentId });
        visit(item.children ?? [], item.id);
      });
    visit(items);
    return result;
  }, [items]);

  const orderedItems = useMemo(() => {
    const selected = new Set(orderedSelectedItems);
    const order = (nodes: TreeItemWithIcon[]): TreeItemWithIcon[] =>
      nodes
        .map(item => ({ ...item, children: order(item.children ?? []) }))
        .sort((left, right) => Number(selected.has(right.id)) - Number(selected.has(left.id)));
    return order(items);
  }, [items, orderedSelectedItems]);

  const toggleSelected = (id: string) => {
    const entry = index.get(id);
    if (!entry) return;
    const next = new Set(selectedItems);
    const select = !next.has(id);
    const update = (item: TreeItemWithIcon) => {
      if (select) next.add(item.id);
      else next.delete(item.id);
      item.children?.forEach(update);
    };
    update(entry.item);
    let parentId = entry.parentId;
    while (parentId) {
      const parent = index.get(parentId);
      if (!parent) break;
      if (parent.item.children?.every(child => next.has(child.id))) next.add(parentId);
      else next.delete(parentId);
      parentId = parent.parentId;
    }
    setSelectedItems?.([...next]);
  };

  return (
    <Select
      label={label}
      value={selectedItems
        .map(itemId => index.get(itemId)?.item.label)
        .filter(Boolean)
        .join(', ')}
      selectedCount={selectedItems.length > 0 ? selectedItems.length : undefined}
      hasValue={selectedItems.length > 0}
      disabled={disabled}
      id={id}
      className={className}
      onOpen={() => {
        setFilter('');
        setOrderedSelectedItems(selectedItems);
      }}
      mode="tree"
      search={{
        value: filter,
        placeholder: intl.formatMessage({ id: 'select.search', defaultMessage: 'Search' }),
        onChange: setFilter,
      }}
      footer={
        <SelectFooter>
          <Button
            priority="secondary"
            onClick={() => setSelectedItems?.(selectedItems.length > 0 ? [] : [...index.keys()])}
            size="sm"
          >
            {selectedItems.length > 0 ? (
              <FormattedMessage id="select.clear-all" defaultMessage="Clear All" />
            ) : (
              <FormattedMessage id="select.select-all" defaultMessage="Select All" />
            )}
          </Button>
          {showConfirmButton && (
            <SelectConfirmButton>
              <FormattedMessage id="select.confirm" defaultMessage="Confirm" />
            </SelectConfirmButton>
          )}
        </SelectFooter>
      }
    >
      <TreeSelectContent
        label={label}
        items={orderedItems}
        selectedItems={selectedItemsSet}
        expandedItems={expandedItemsSet}
        search={filter}
        onToggleExpanded={itemId =>
          setExpandedItems(current =>
            current.includes(itemId) ? current.filter(value => value !== itemId) : [...current, itemId]
          )
        }
        onToggleSelected={toggleSelected}
      />
    </Select>
  );
}
